/* ============================================================
   MOSAIK — MES PROJETS  (user-saved designs)

   Lets a visitor save the design they built and find it again later. Two rules
   drive the UX:
     • The "Enregistrer" button is ALWAYS visible (bottom action bar, left of
       "Afficher les détails") — a logged-out visitor who clicks it is nudged to
       log in / create an account.
     • The saved list lives in the "Mes projets" sidebar card. Logged out, that
       card carries the same message the other way round: an account is what
       makes saving possible.

   Identity is established server-side: requests go through the Shopify App
   Proxy (window.MOSAIK_PROXY_BASE), which appends a signed logged_in_customer_id
   the Worker verifies. The browser never asserts who it is — so a saved design
   is bound to a real Shopify customer, not to anything the page could forge.

   Une exception assumée à cette règle du compte : ?template=<id> dans l'URL
   ouvre la création correspondante, même chez un autre client ou un visiteur
   sans compte — c'est ce qui rend l'adresse partageable. La lecture reste bornée
   à cet id (jamais une liste), et l'id est un UUID aléatoire : c'est le lien qui
   fait l'autorisation. Voir openFromUrl / getShared, et getSharedTemplate côté
   Worker (backend/src/worker.js).

   Storage format (self-contained, so this works even when the curated template
   library — mosaik-templates.js — is not loaded):
     { collection, width, height, cols, rows,
       tiles   : [pileIdx|null, ...]      row-major, length cols*rows
       borders : { cellIdx: {b:borderIdx, side} }
       corners : { cornerIdx: cornerIdx }
       lights  : serialized cable | null }
   ============================================================ */
(function(){

  const PENDING_KEY = 'mosaik_pending_template';   // survives the login redirect
  // ?template=<id> — deux provenances, un seul traitement (voir openFromUrl) :
  //   • le lien « Ouvrir dans le configurateur » de la page compte
  //     (sections/mosaik-account-templates.liquid) ;
  //   • une URL partagée, ouverte par quelqu'un d'autre que le créateur — voire
  //     par un visiteur sans compte.
  const URL_PARAM   = 'template';

  /* Libellés propres à l'ouverture par lien. Ils vivent ici plutôt que dans
     mosaik-data.js parce qu'ils ne servent qu'à ce chemin-là. Même contrat que
     mosaik.getText : la clé EST la version anglaise, seul le français est
     traduit — les autres langues retombent sur la clé. */
  const TXT_FR = {
    // « la création » et non « votre création » : le lien peut venir de
    // quelqu'un d'autre.
    'Opening the design…'                 : 'Ouverture de la création…',
    'This design is no longer available.' : 'Cette création n’est plus disponible.',
    'Could not load this design.'         : 'Impossible de charger cette création.'
  };

  /* Card actions are icon-only (see refreshList) — same stroke style as the
     eraser icon in mosaik-elements.js so the whole UI reads as one set. */
  const SVG_ATTRS = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" '
    + 'stroke-width="1.9" stroke-linejoin="round" stroke-linecap="round"';
  const ICON_APPLY  = '<svg ' + SVG_ATTRS + '><path d="M20 6L9 17l-5-5"/></svg>';
  const ICON_DELETE = '<svg ' + SVG_ATTRS + '><path d="M3 6h18"/><path d="M8 6V4h8v2"/>'
    + '<path d="M6 6l1 14h10l1-14"/><path d="M10 10v7M14 10v7"/></svg>';
  // floppy disk = "save", the same idea as the 💾 on the Save button. Shown in
  // the sign-in prompt: the popup is about saving, not about a locked door.
  const ICON_SAVE = '<svg ' + SVG_ATTRS + '><path d="M4 5.5A1.5 1.5 0 015.5 4h10L20 8.5v10a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 18.5v-13z"/>'
    + '<path d="M8 4v5h7V4"/><path d="M7.5 20v-6h9v6"/></svg>';

  window.MosaikMyTemplates = {

    /* ---------- config read from the page ---------- */
    proxyBase:function(){ return (window.MOSAIK_PROXY_BASE || '/apps/mosaik').replace(/\/+$/, ''); },
    isLoggedIn:function(){ return !!window.MOSAIK_CUSTOMER_ID; },

    /* mosaik.getText, augmenté des libellés locaux (TXT_FR). */
    text:function(s){
      const m = window.mosaik;
      const lang = (m && m.options) ? m.options.lang : 'fr';
      if(lang === 'fr' && TXT_FR[s]) return TXT_FR[s];
      return m ? m.getText(s) : s;
    },

    /* ---------- ?template=<id> ----------
       Lu par MosaikCollection.setup() aussi : la présence d'un id est ce qui lui
       dit de ne pas forcer le popup de configuration, puisque le plan qu'on
       s'apprête à restaurer porte déjà sa collection et ses dimensions. */
    readUrlTemplateId:function(){
      try { return new URLSearchParams(window.location.search).get(URL_PARAM) || null; }
      catch(e){ return null; }
    },
    clearUrlTemplateId:function(){
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete(URL_PARAM);
        history.replaceState(null, '', url.toString());
      } catch(e){ /* older browsers: ignore */ }
    },

    /* ---------- serialize the LIVE design ----------
       Same shape as MosaikTemplates.exportCurrent(), plus the exact width/height
       so the design can be restored pixel-for-pixel (not just size-agnostically). */
    serialize:function(){
      const m = window.mosaik, colKey = m.options.currentCollection, col = m.collections[colKey];
      const width  = parseFloat(document.getElementById('mosaik-area-width').value)  || 0;
      const height = parseFloat(document.getElementById('mosaik-area-height').value) || 0;
      const cols = Math.ceil(m.options.width  / m.options.mosaikWidth);
      const rows = Math.ceil(m.options.height / m.options.mosaikHeight);
      const piles = col.piles;
      const tiles = [];
      // -1 = dalle SUPPRIMÉE (la gomme), à distinguer de null (case jamais peinte,
      // qui repart de la couleur de fond) : sans ça un rechargement rebouchait les
      // trous du plan avec des dalles noires.
      for(let i = 0; i < cols * rows; i++){
        const c = m.result[i];
        tiles.push(c ? piles.indexOf(c) : (c === false ? -1 : null));
      }
      const findIdx = (arr, o) => arr.findIndex(x => x.color === o.color && x.name_fr === o.name_fr);
      const borders = {};
      Object.entries(m.borderAssignments).forEach(([idx, bo]) => { borders[idx] = { b: findIdx(col.borders, bo), side: bo.side }; });
      const corners = {};
      Object.entries(m.cornerAssignments).forEach(([idx, co]) => { corners[idx] = findIdx(col.corners, co); });
      const lights = (window.MosaikLights && MosaikLights.enabled) ? MosaikLights.serializeChain() : null;
      // objets posés (emplacements véhicule, accessoires) — mosaik-objects.js
      const objects = (window.MosaikObjects && MosaikObjects.enabled) ? MosaikObjects.serialize() : null;
      return { collection: colKey, width, height, cols, rows, tiles, borders, corners, lights, objects };
    },

    /* idx+side-only stub so buildBorderCells gives stable idx/side without pixels. */
    _geomStub:function(cols, rows){
      return { cols, rows, tileW:1, tileH:1, bW:1, bH:1, xOff:0, yOff:0, cW:cols+2, cH:rows+2 };
    },

    /* ---------- restore a saved design onto the canvas (exact) ----------
       Sets the dimension inputs + collection first, so the live grid matches the
       stored tiles array, then lays tiles / borders / corners exactly as saved. */
    apply:function(design, opts){
      opts = opts || {};
      const m = window.mosaik;
      const col = m.collections[design.collection];
      if(!col){ console.warn('[MosaikMyTemplates] unknown collection:', design.collection); return false; }

      // guard against silently wiping an in-progress design (skipped for the
      // post-login auto-restore, where the visitor asked for exactly this).
      if(!opts.silent){
        const hasDesign = (m.result && m.result.some(Boolean))
                       || Object.keys(m.borderAssignments).length
                       || Object.keys(m.cornerAssignments).length;
        if(hasDesign && !window.confirm(m.getText('Replace the current design?'))) return false;
      }

      // 1) restore the exact dimensions
      const wInput = document.getElementById('mosaik-area-width');
      const hInput = document.getElementById('mosaik-area-height');
      if(wInput && design.width)  wInput.value = design.width;
      if(hInput && design.height) hInput.value = design.height;

      // 2) collection + rebuild its UI (mirrors the collection-change handler)
      const sel = document.getElementById('mosaik-collection');
      if(sel) sel.value = design.collection;
      m.options.checkerboard_piles = [];
      m.result = [];
      m.borderAssignments = {};
      m.cornerAssignments = {};
      if(window.MosaikLights) MosaikLights.clear(m);
      if(window.MosaikObjects) MosaikObjects.clear(m);
      m.setCollection();
      if(window.MosaikObjects) MosaikObjects.buildColors();
      const atc = document.getElementById('mosaik-add-to-cart');
      if(atc) atc.style.display = (design.collection === 'Polypropylene') ? 'inline' : 'none';

      // options.width/height are only refreshed by draw(); recompute the grid the
      // same way so tiles/borders land on the right cells.
      const cols = Math.ceil((parseFloat(wInput ? wInput.value : 0) || 0) / m.options.mosaikWidth);
      const rows = Math.ceil((parseFloat(hInput ? hInput.value : 0) || 0) / m.options.mosaikHeight);

      // 3) tiles
      const piles = col.piles;
      m.result = [];
      (design.tiles || []).forEach((p, idx) => {
        if(p === -1) m.result[idx] = false;             // dalle supprimée (voir serialize)
        else if(p != null && piles[p]) m.result[idx] = piles[p];
      });

      // 4) borders (explicit {cellIdx:{b,side}})
      Object.entries(design.borders || {}).forEach(([idx, b]) => {
        const bo = col.borders[b.b];
        if(bo) m.borderAssignments[idx] = { ...bo, side: b.side };
      });

      // 5) corners (explicit {cornerIdx: cornerIdx})
      Object.entries(design.corners || {}).forEach(([idx, c]) => {
        const co = col.corners[c];
        if(co) m.cornerAssignments[idx] = co;
      });

      // 6) lights — only when the feature is actually live
      if(design.lights && design.lights.length && window.MosaikLights && MosaikLights.enabled){
        MosaikLights.loadChain(m, design.lights, cols);
        MosaikLights.updateStatus(m);
      }

      // 7) objets posés (emplacements véhicule, accessoires) — idem
      if(design.objects && design.objects.length && window.MosaikObjects && MosaikObjects.enabled)
        MosaikObjects.load(m, design.objects);

      m.draw();

      // le dessin rechargé remplace tout (collection et dimensions comprises) :
      // l'historique repart de lui, les entrées d'avant ne sont plus rejouables
      if(window.MosaikHistory) MosaikHistory.resetHistory();

      // keep the setup header label in sync with what's now on the canvas
      if(window.MosaikCollection){
        try {
          MosaikCollection._selected = design.collection;
          if(MosaikCollection.setSummary) MosaikCollection.setSummary(design.collection);
        } catch(e){}
      }
      return true;
    },

    /* ---------- gallery thumbnail (color-swatch mini-map) ---------- */
    renderThumb:function(cv, design){
      const m = window.mosaik, col = m.collections[design.collection];
      if(!col) return;
      const cols = design.cols || 6, rows = design.rows || 4;
      const tp = Math.max(3, Math.floor(150 / Math.max(cols, rows)));
      const mg = Math.max(2, Math.floor(tp / 3));
      cv.width = cols * tp + 2 * mg; cv.height = rows * tp + 2 * mg;
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, cv.width, cv.height);
      const piles = col.piles, tiles = design.tiles || [];
      for(let r = 0; r < rows; r++) for(let c = 0; c < cols; c++){
        const p = tiles[r * cols + c];
        ctx.fillStyle = (p != null && piles[p]) ? piles[p].color : '#eee';
        ctx.fillRect(mg + c * tp, mg + r * tp, tp, tp);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 1;
        ctx.strokeRect(mg + c * tp, mg + r * tp, tp, tp);
      }
    },

    /* ---------- API (via the App Proxy, same-origin so the Shopify session
                     cookie rides along and the Worker sees the customer) ---------- */
    /* Analytics ride along in the query string: Shopify's App Proxy signs every
       param it forwards, and the Worker writes the counters itself once the
       signature checks out — so the numbers can't be inflated from the page.
       vid/sid are the same opaque ids mosaik-stats.js uses, which is what lets a
       saved design join the rest of the funnel. */
    _params:function(extra){
      const p = [];
      try {
        if(window.MosaikStats){ p.push('vid=' + encodeURIComponent(MosaikStats.vid()),
                                       'sid=' + encodeURIComponent(MosaikStats.sid())); }
      } catch(e){}
      Object.entries(extra || {}).forEach(([k, v]) => {
        if(v != null) p.push(k + '=' + encodeURIComponent(v));
      });
      return p.length ? '?' + p.join('&') : '';
    },
    _track:function(event, extra){
      try { if(window.MosaikStats) MosaikStats.track(event, extra); } catch(e){}
    },
    _fetch:function(path, options){
      return fetch(this.proxyBase() + path, Object.assign({ credentials: 'same-origin' }, options));
    },
    list:function(){
      return this._fetch('/templates' + this._params(), { method: 'GET' })
        .then(r => r.ok ? r.json() : { templates: [] })
        .then(d => d.templates || []);
    },
    // opts.intent = 'save_button' when this save is the tail end of the
    // logged-out → login/signup → back-here flow (see resumePending). That flag
    // is how the backend attributes a brand-new customer to the Save button.
    save:function(name, design, opts){
      return this._fetch('/templates' + this._params({ intent: (opts || {}).intent }), {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },   // CORS-simple, matches stats.js
        body: JSON.stringify({ name: name, design: design })
      }).then(r => r.json().catch(() => ({})).then(body => ({ ok: r.ok, status: r.status, body })));
    },
    remove:function(id){
      return this._fetch('/templates' + this._params({ id: id }), { method: 'DELETE' });
    },
    /* Une création par son id, sans condition de propriétaire ni de compte —
       c'est ce qui fait marcher une URL partagée.
       Résout sur le plan, ou sur null quand l'id est inconnu (404 : création
       supprimée, ou lien erroné). Rejette sur tout le reste — réseau coupé,
       Worker en erreur — pour que l'appelant sache distinguer « cette création
       n'existe plus » de « je n'ai pas réussi à la charger ». */
    getShared:function(id){
      return this._fetch('/templates' + this._params({ shared: id }), { method: 'GET' })
        .then(r => {
          if(r.status === 404) return null;
          if(!r.ok) throw new Error('shared ' + r.status);
          return r.json().then(d => (d && d.template) ? d.template : null);
        });
    },

    /* ---------- Save button ---------- */
    onSaveClick:function(){
      const m = window.mosaik;
      const t = s => m.getText(s);
      // nothing to save until a collection/design exists (setup popup still open)
      if(!m.options.currentCollection || !m.collections[m.options.currentCollection]) return;

      // denominator of the save funnel; the logged-out share is what the
      // login/signup attribution is measured against
      this._track('template_save_click', { loggedIn: this.isLoggedIn() });

      // logged out: tell them a sign-in is required BEFORE anything else, via a
      // styled popup (matches the page, unlike the native confirm). On accept,
      // keep the design across the login redirect so it is saved automatically
      // on return (see resumePending on boot).
      if(!this.isLoggedIn()){
        this.confirmDialog({
          iconSvg: ICON_SAVE,
          title: t('Save your creations'),
          message: t('Log in or create an account to save your design and find it again later.'),
          okLabel: t('Log in / Sign up'),
          cancelLabel: t('Cancel')
        }).then(ok => { if(ok) this.gotoLogin(true); });
        return;
      }

      // logged in: name the design, then save it
      const design = this.serialize();
      const def = t('My template');
      const name = (window.prompt(t('Name your design'), def) || def).trim() || def;
      this.save(name, design).then(res => {
        if(res.ok){ this.toast(t('Design saved')); this.refreshList(); }
        else if(res.status === 409) this.toast(t('You have reached the saved-designs limit.'));
        else this.toast(t('Could not save — please try again.'));
      }).catch(() => this.toast(t('Could not save — please try again.')));
    },

    /* ---------- send the visitor to log in / sign up ----------
       `stash` keeps the live design in localStorage across the redirect so
       resumePending() can restore AND save it on the way back. The Save button
       passes true (the visitor asked to save); the "Mes projets" sign-in button
       passes false — they only asked to log in, so nothing is saved for them. */
    gotoLogin:function(stash){
      const m = window.mosaik, t = s => m.getText(s);
      this._track('template_login_redirect');
      if(stash){
        try { localStorage.setItem(PENDING_KEY, JSON.stringify({ name: t('My template'), design: this.serialize() })); } catch(e){}
      }
      const login = window.MOSAIK_LOGIN_URL || '/account/login';
      const ret = encodeURIComponent(location.pathname + location.search);
      window.location.href = login + (login.indexOf('?') === -1 ? '?' : '&') + 'return_url=' + ret;
    },

    /* ---------- resume a save started before the login redirect ----------
       Runs AFTER the rest of the bootstrap (setTimeout in setupUI), so it wins
       over MosaikCollection.setup(): that either applies a ?collection= URL setup
       or forces the mandatory setup popup — both would otherwise reset the canvas
       we are about to restore. We apply the saved design last, dismiss/neutralize
       the setup popup, then persist to the backend. */
    resumePending:function(){
      if(!this.isLoggedIn()) return;
      let pending;
      try { pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); } catch(e){ pending = null; }
      if(!pending || !pending.design) return;
      const m = window.mosaik, t = s => m.getText(s);

      const shown = this.apply(pending.design, { silent: true });   // show them their design back
      // take over the setup flow so the collection popup doesn't re-reset it
      if(shown) this.adoptSetup(pending.design.collection);

      // intent: this save closes the loop opened by a logged-out click on the
      // Save button, so the backend can credit a first-time customer to it.
      this.save(pending.name, pending.design, { intent: 'save_button' }).then(res => {
        if(res.ok) this.toast(t('Design saved'));
      }).catch(() => {}).then(() => {
        try { localStorage.removeItem(PENDING_KEY); } catch(e){}
        this.refreshList();
      });
    },

    /* ---------- prendre la main sur le flux de configuration ----------
       Un plan restauré porte déjà sa collection ET ses dimensions : le popup de
       configuration n'a plus rien à demander, et s'il s'ouvrait il repartirait
       d'un canvas vide. Appelé après chaque restauration — reprise d'une
       sauvegarde interrompue par la connexion, ou ouverture depuis un lien. */
    adoptSetup:function(collection){
      if(window.MosaikCollection){
        try {
          MosaikCollection._hasChoice = true;
          MosaikCollection._mandatory = false;
          MosaikCollection._selected  = collection;
          MosaikCollection.close();
          if(MosaikCollection.setSummary) MosaikCollection.setSummary(collection);
        } catch(e){}
      }
      if(window.MosaikStats) MosaikStats.setupApplied();
    },

    /* Contrepartie de la mise en veille du popup par MosaikCollection.setup() :
       quand l'ouverture échoue, c'est à nous de le rouvrir, sinon le visiteur
       reste devant un plan vide sans aucun moyen de démarrer. Le test sur
       _hasChoice évite de le rouvrir par-dessus une configuration déjà en place
       (?collection= dans l'URL, ou un resumePending qui a abouti). */
    fallbackSetup:function(){
      if(window.MosaikCollection && !MosaikCollection._hasChoice) MosaikCollection.open(true);
    },

    /* ---------- ouvrir une création depuis ?template=<id> ----------
       Deux usages, le même chemin :
         • le lien « Ouvrir dans le configurateur » de la page compte
           (sections/mosaik-account-templates.liquid), qui n'a pas de canvas et
           ne peut donc transmettre qu'un id ;
         • une URL partagée à quelqu'un d'autre — d'où la lecture par
           MosaikMyTemplates.getShared, qui interroge le backend sur cet id seul
           et non sur la liste de l'appelant. Aucune connexion n'est demandée :
           le destinataire n'a en général pas de compte, et en exiger un ferait
           du lien une porte fermée. Ce qui protège la création reste son id,
           un UUID aléatoire (cf. getSharedTemplate côté Worker).

       `silent` : pas de « remplacer la création actuelle ? ». Le visiteur a
       ouvert CETTE création, et le canvas est vierge à ce stade.

       Renvoie true dès qu'un id était présent, que l'ouverture aboutisse ou non :
       l'appelant sait ainsi que ce chargement est pris en charge. */
    openFromUrl:function(){
      const id = this.readUrlTemplateId();
      if(!id) return false;
      const self = this, t = s => self.text(s);

      // Un seul coup : le paramètre part maintenant, pour qu'un rechargement ne
      // rejoue pas l'ouverture et que l'URL partageable qu'écrira
      // MosaikCollection.updateUrl soit celle d'un plan normal.
      this.clearUrlTemplateId();

      this._track('template_open_link');
      this.toast(t('Opening the design…'));

      this.getShared(id).then(tpl => {
        // introuvable = supprimée entre-temps, ou lien erroné ; collection
        // inconnue = plan enregistré avec une gamme qui n'est plus au catalogue.
        // Le visiteur voit le même message dans les deux cas, mais la console
        // dit lequel : c'est la seule trace exploitable sur une boutique en
        // ligne, où l'on ne peut pas rejouer le chargement pas à pas.
        if(!tpl || !tpl.design){
          console.warn('[MosaikMyTemplates] création introuvable côté backend:', id);
        } else if(!self.apply(tpl.design, { silent: true })){
          console.warn('[MosaikMyTemplates] plan reçu mais refusé — collection absente de cette page:',
                       tpl.design.collection, Object.keys((window.mosaik || {}).collections || {}));
        } else {
          self.adoptSetup(tpl.design.collection);
          return;
        }
        self.toast(t('This design is no longer available.'));
        self.fallbackSetup();
      }).catch(err => {
        console.warn('[MosaikMyTemplates] lecture du lien en échec:', id, err && err.message);
        self.toast(t('Could not load this design.'));
        self.fallbackSetup();
      });

      return true;
    },

    /* ---------- "Mes projets" sidebar card ----------
       One entry point, two states: the saved list when a customer is logged in,
       and otherwise the reason there is no list — saving needs an account. Both
       are (re)built from scratch, so calling this after a save / delete / login
       is always enough to bring the card back in sync. */
    refreshList:function(){
      const host = document.getElementById('mosaik-projects');
      if(!host) return;
      const m = window.mosaik, t = s => m.getText(s), self = this;
      host.innerHTML = '';

      if(!this.isLoggedIn()){
        const msg = document.createElement('div');
        msg.className = 'mosaik-proj-signin';
        msg.innerText = t('Log in to save your designs and find them here.');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.id = 'mosaik-projects-login';
        btn.className = 'mosaik-bar-btn';
        btn.innerText = t('Log in / Sign up');
        btn.addEventListener('click', () => self.gotoLogin(false));
        host.appendChild(msg); host.appendChild(btn);
        return;
      }

      const grid = document.createElement('div');
      grid.className = 'mosaik-proj-grid';
      grid.id = 'mosaik-projects-grid';
      grid.innerHTML = '<div class="mosaik-proj-empty">' + t('Loading…') + '</div>';
      host.appendChild(grid);

      this.list().then(templates => {
        grid.innerHTML = '';
        if(!templates.length){
          grid.innerHTML = '<div class="mosaik-proj-empty">' + t('No saved designs yet.') + '</div>';
          return;
        }
        templates.forEach(tpl => { const c = self.projectCard(tpl); if(c) grid.appendChild(c); });
      });
    },

    /* one saved design: thumbnail (click = apply) + name, size and icon actions */
    projectCard:function(tpl){
      const design = tpl.design; if(!design) return null;
      const m = window.mosaik, t = s => m.getText(s), self = this;
      const card = document.createElement('div');
      card.className = 'mosaik-proj';

      const cv = document.createElement('canvas');
      cv.title = t('Apply this design');
      cv.addEventListener('click', () => self.apply(design));
      card.appendChild(cv);

      const name = document.createElement('div');
      name.className = 'mosaik-proj-name';
      name.innerText = tpl.name || t('My template');
      card.appendChild(name);

      const dims = document.createElement('div');
      dims.className = 'mosaik-proj-dims';
      dims.innerText = (design.width || '?') + ' × ' + (design.height || '?') + ' m';
      card.appendChild(dims);

      const actions = document.createElement('div');
      actions.className = 'mosaik-proj-actions';
      // icon-only buttons: the label lives in title/aria-label so the card stays
      // compact and the meaning survives translation.
      const applyBtn = self.iconButton(ICON_APPLY, t('Apply'), '#699bb4');
      applyBtn.addEventListener('click', () => self.apply(design));
      const delBtn = self.iconButton(ICON_DELETE, t('Delete'), '#b4534f');
      delBtn.addEventListener('click', () => {
        if(!window.confirm(t('Delete this design?'))) return;
        self.remove(tpl.id).then(() => self.refreshList());
      });
      actions.appendChild(applyBtn); actions.appendChild(delBtn);
      card.appendChild(actions);

      self.renderThumb(cv, design);
      return card;
    },

    /* ---------- round icon button (label kept as tooltip / aria-label) ---------- */
    iconButton:function(svg, label, color){
      const b = document.createElement('button');
      b.type = 'button';
      b.title = label;
      b.setAttribute('aria-label', label);
      b.innerHTML = svg;
      b.style.cssText = 'border:1px solid #e3e3e3;background:#fff;color:' + color + ';'
        + 'width:32px;height:32px;padding:0;border-radius:50%;cursor:pointer;'
        + 'display:inline-flex;align-items:center;justify-content:center;transition:all .15s;';
      const sv = b.firstChild;
      if(sv && sv.style){ sv.style.width = '17px'; sv.style.height = '17px'; sv.style.display = 'block'; }
      b.onmouseover = () => { b.style.background = color; b.style.borderColor = color; b.style.color = '#fff'; };
      b.onmouseout  = () => { b.style.background = '#fff'; b.style.borderColor = '#e3e3e3'; b.style.color = color; };
      return b;
    },

    /* ---------- styled confirm popup (page-matching, replaces window.confirm)
       Returns a Promise<boolean>: true = primary action, false = cancel/dismiss.
       Built in JS so no liquid markup is required. ---------- */
    confirmDialog:function(opts){
      opts = opts || {};
      return new Promise(resolve => {
        const ov = document.createElement('div');
        ov.style.cssText = 'position:fixed;inset:0;background:rgba(36,51,58,.55);z-index:10001;'
          + 'display:flex;align-items:center;justify-content:center;padding:20px;opacity:0;transition:opacity .18s;';

        const card = document.createElement('div');
        card.style.cssText = 'background:#fff;max-width:420px;width:100%;border-radius:16px;padding:30px 26px 22px;'
          + 'box-shadow:0 18px 50px rgba(0,0,0,.28);text-align:center;'
          + 'font-family:Arial,Helvetica,sans-serif;transform:translateY(10px);transition:transform .18s;';

        // iconSvg = inline markup shown in a tinted round badge; icon = plain
        // text/emoji. Callers pass one or the other, never both.
        if(opts.iconSvg){
          const ic = document.createElement('div');
          ic.style.cssText = 'width:58px;height:58px;margin:0 auto 16px;border-radius:50%;'
            + 'background:#eaf2f6;color:#699bb4;display:flex;align-items:center;justify-content:center;';
          ic.innerHTML = opts.iconSvg;
          const sv = ic.firstChild;
          if(sv && sv.style){ sv.style.width = '28px'; sv.style.height = '28px'; sv.style.display = 'block'; }
          card.appendChild(ic);
        } else if(opts.icon){
          const ic = document.createElement('div');
          ic.style.cssText = 'font-size:2.4em;line-height:1;margin-bottom:14px;';
          ic.innerText = opts.icon;
          card.appendChild(ic);
        }
        if(opts.title){
          const h = document.createElement('div');
          h.style.cssText = 'font-size:1.2em;font-weight:700;color:#3a4a52;margin-bottom:10px;';
          h.innerText = opts.title;
          card.appendChild(h);
        }
        const p = document.createElement('div');
        p.style.cssText = 'color:#6a7a82;font-size:.98em;line-height:1.5;margin-bottom:24px;white-space:pre-line;';
        p.innerText = opts.message || '';
        card.appendChild(p);

        // One row, always: "Se connecter / Créer un compte" is long, so the
        // primary button takes the leftover width (and wraps inside itself on a
        // narrow phone) instead of dropping under the Cancel button.
        const actions = document.createElement('div');
        actions.style.cssText = 'display:flex;gap:10px;justify-content:center;flex-wrap:nowrap;align-items:stretch;';

        const okBtn = document.createElement('button');
        okBtn.type = 'button';
        okBtn.innerText = opts.okLabel || 'OK';
        okBtn.style.cssText = 'background:#699bb4;color:#fff;border:0;min-height:46px;border-radius:26px;'
          + 'font-weight:700;padding:10px 20px;cursor:pointer;font-size:1em;line-height:1.25;'
          + 'flex:1 1 auto;min-width:0;'
          + 'box-shadow:0 8px 20px rgba(105,155,180,.28);transition:all .15s;';
        okBtn.onmouseover = () => { okBtn.style.background = '#5a8aa2'; };
        okBtn.onmouseout  = () => { okBtn.style.background = '#699bb4'; };

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.innerText = opts.cancelLabel || 'Annuler';
        cancelBtn.style.cssText = 'background:#fafafa;color:#666;border:2px solid #ccc;min-height:46px;border-radius:26px;'
          // narrow padding on purpose: every pixel not spent here is a pixel the
          // long primary label gets, which is what keeps the row to one line.
          + 'font-weight:700;padding:10px 16px;cursor:pointer;font-size:1em;line-height:1.25;'
          + 'flex:0 0 auto;white-space:nowrap;transition:all .15s;';
        cancelBtn.onmouseover = () => { cancelBtn.style.borderColor = '#699bb4'; cancelBtn.style.color = '#2c6b86'; };
        cancelBtn.onmouseout  = () => { cancelBtn.style.borderColor = '#ccc'; cancelBtn.style.color = '#666'; };

        let done = false;
        const finish = val => {
          if(done) return; done = true;
          ov.style.opacity = '0'; card.style.transform = 'translateY(10px)';
          document.removeEventListener('keydown', onKey);
          setTimeout(() => { if(ov.parentNode) ov.parentNode.removeChild(ov); }, 180);
          resolve(val);
        };
        const onKey = e => { if(e.key === 'Escape') finish(false); else if(e.key === 'Enter') finish(true); };

        okBtn.addEventListener('click', () => finish(true));
        cancelBtn.addEventListener('click', () => finish(false));
        ov.addEventListener('click', e => { if(e.target === ov) finish(false); });
        document.addEventListener('keydown', onKey);

        actions.appendChild(cancelBtn);
        actions.appendChild(okBtn);
        card.appendChild(actions);
        ov.appendChild(card);
        document.body.appendChild(ov);
        requestAnimationFrame(() => { ov.style.opacity = '1'; card.style.transform = 'translateY(0)'; });
      });
    },

    /* ---------- tiny toast ---------- */
    toast:function(msg){
      let el = document.getElementById('mosaik-toast');
      if(!el){
        el = document.createElement('div');
        el.id = 'mosaik-toast';
        el.style.cssText = 'position:fixed;left:50%;bottom:28px;transform:translateX(-50%);'
          + 'background:#24333a;color:#fff;padding:10px 18px;border-radius:8px;font-size:14px;'
          + 'z-index:10000;box-shadow:0 4px 16px rgba(0,0,0,.25);opacity:0;transition:opacity .2s;';
        document.body.appendChild(el);
      }
      el.innerText = msg;
      el.style.opacity = '1';
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => { el.style.opacity = '0'; }, 2600);
    },

    /* ---------- UI wiring (called from the controller bootstrap) ---------- */
    setupUI:function(mosaik){
      const self = this, t = s => mosaik.getText(s);

      const saveBtn = document.getElementById('mosaik-save-tpl');
      if(saveBtn){
        const sp = saveBtn.querySelector('span');
        if(sp) sp.innerText = t('Save design'); else saveBtn.innerText = '💾 ' + t('Save design');
        saveBtn.addEventListener('click', () => self.onSaveClick());
      }

      const title = document.getElementById('mosaik-card-projects-title');
      if(title) title.innerText = t('My projects');
      self.refreshList();

      // Finish any save interrupted by the login redirect. Deferred so it runs
      // AFTER MosaikCollection.setup() (which is wired later in the same
      // bootstrap) — otherwise the setup popup would reset the restored canvas.
      setTimeout(() => {
        self.resumePending();
        // Puis l'ouverture par lien. Les deux ne se marchent pas dessus : l'apply
        // de resumePending est synchrone, celui d'openFromUrl arrive après un
        // aller-retour réseau — donc en dernier, et c'est bien ce qu'on veut (le
        // visiteur a cliqué sur cette création-là), pendant que resumePending
        // enregistre quand même le travail commencé avant la connexion.
        self.openFromUrl();
      }, 0);
    }
  };

})();
